/**
 * 多项目作业右上角悬浮进度指示器（方案 §需求4）。
 *
 * 职责：
 * - 固定在页面右上角（仿 antd message 浮层风格，但置右上）；
 * - 从全局 store 读取活跃作业列表，堆叠展示每个作业的进度 + 取消按钮；
 * - 无活跃作业时不渲染（不占用空间）。
 *
 * 挂载位置：App.tsx 的 AntdApp 内（与 MultiProjectJobStoreProvider 同级）。
 */
import React, { useState } from 'react';
import { Progress, Button, Tag, Tooltip } from 'antd';
import {
  CheckCircleFilled,
  CloseCircleFilled,
  LoadingOutlined,
  CloseOutlined,
  ExclamationCircleFilled,
  DownOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { useMultiProjectJobStore } from '@/modules/detection-task/context/MultiProjectJobStoreContext';
import type { MultiProjectJobStatus } from '@/modules/detection-task/services/multiProjectTaskApi';

function statusMeta(status: MultiProjectJobStatus['status'] | undefined) {
  switch (status) {
    case 'completed':
      return { color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0', icon: <CheckCircleFilled style={{ color: '#16a34a' }} />, label: '已完成' };
    case 'failed':
      return { color: '#dc2626', bg: '#fef2f2', border: '#fecaca', icon: <CloseCircleFilled style={{ color: '#dc2626' }} />, label: '失败' };
    case 'cancelled':
      return { color: '#d97706', bg: '#fffbeb', border: '#fde68a', icon: <ExclamationCircleFilled style={{ color: '#d97706' }} />, label: '已取消' };
    case 'pending':
      return { color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe', icon: <LoadingOutlined style={{ color: '#2563eb' }} />, label: '排队中' };
    case 'running':
    default:
      return { color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe', icon: <LoadingOutlined style={{ color: '#2563eb' }} />, label: '进行中' };
  }
}

function progressText(job: { status: MultiProjectJobStatus | null; meta: { selectedProjectIds: string[] } }): string {
  const status = job.status;
  if (!status) return '正在查询进度...';
  if (status.status === 'completed') {
    return `共创建 ${status.tasks_created} 个任务`;
  }
  if (status.status === 'failed') {
    return status.error_message || '作业失败';
  }
  if (status.status === 'cancelled') {
    return '作业已取消';
  }
  // running / pending
  return `进度：${status.projects_processed}/${status.projects_total} 项目，已创建 ${status.tasks_created} 任务`;
}

export const MultiProjectJobFloatingIndicator: React.FC = () => {
  const { jobs, cancelJob } = useMultiProjectJobStore();
  // 折叠状态：默认展开；仅用户点击切换，不随 jobs 变化重置（收起后新作业到来仍保持收起）
  const [collapsed, setCollapsed] = useState(false);

  if (jobs.length === 0) return null;

  const runningCount = jobs.filter(
    (j) => j.status?.status === 'running' || j.status?.status === 'pending',
  ).length;
  const overviewText = runningCount > 0
    ? `多项目作业 ${runningCount} 个进行中`
    : `多项目作业 ${jobs.length} 个`;

  return (
    <div
      style={{
        position: 'fixed',
        top: 24,
        right: 24,
        zIndex: 2000,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        maxWidth: 360,
        pointerEvents: 'auto',
      }}
    >
      {collapsed ? (
        // 折叠态：小条概览（点击展开）
        <div
          onClick={() => setCollapsed(false)}
          style={{
            background: '#eff6ff',
            border: '1px solid #bfdbfe',
            borderRadius: 12,
            padding: '10px 14px',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
            minWidth: 220,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            cursor: 'pointer',
          }}
        >
          <LoadingOutlined style={{ color: '#2563eb' }} />
          <span style={{ fontSize: 13, fontWeight: 600, color: '#2563eb', flex: 1 }}>
            {overviewText}
          </span>
          <RightOutlined style={{ color: '#94a3b8', fontSize: 12 }} />
        </div>
      ) : (
        <>
          {/* 顶部收起条：概览 + 收起按钮 */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '6px 10px',
              marginBottom: 2,
            }}
          >
            <LoadingOutlined style={{ color: '#2563eb', fontSize: 13 }} />
            <span style={{ fontSize: 13, fontWeight: 600, color: '#475569', flex: 1 }}>
              {overviewText}
            </span>
            <Tooltip title="收起">
              <Button
                type="text"
                size="small"
                icon={<DownOutlined />}
                onClick={() => setCollapsed(true)}
                style={{ color: '#94a3b8' }}
              />
            </Tooltip>
          </div>
          {jobs.map((job) => {
            const status = job.status;
            const meta = statusMeta(status?.status);
            const percent =
              status && status.projects_total > 0
                ? Math.round((status.projects_processed / status.projects_total) * 100)
                : 0;
            const isTerminal = status?.status === 'completed' || status?.status === 'failed' || status?.status === 'cancelled';
            const isRunning = status?.status === 'running' || status?.status === 'pending';

            return (
              <div
                key={job.jobId}
                style={{
                  background: meta.bg,
                  border: `1px solid ${meta.border}`,
                  borderRadius: 12,
                  padding: '14px 16px',
                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
                  minWidth: 280,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  {meta.icon}
                  <span style={{ fontSize: 13, fontWeight: 600, color: meta.color, flex: 1 }}>
                    多项目检测任务
                  </span>
                  <Tag color={status?.status === 'completed' ? 'success' : status?.status === 'failed' ? 'error' : status?.status === 'cancelled' ? 'warning' : 'processing'} style={{ margin: 0 }}>
                    {meta.label}
                  </Tag>
                  {isRunning && (
                    <Tooltip title="取消作业">
                      <Button
                        type="text"
                        size="small"
                        icon={<CloseOutlined />}
                        onClick={() => void cancelJob(job.jobId)}
                        style={{ color: '#94a3b8' }}
                      />
                    </Tooltip>
                  )}
                </div>

                <div style={{ fontSize: 12, color: '#475569', marginBottom: 8 }}>
                  {progressText(job)}
                </div>

                {!isTerminal && (
                  <Progress
                    percent={percent}
                    status={status?.status === 'pending' ? 'normal' : 'active'}
                    size="small"
                    strokeColor={{ from: '#3b82f6', to: '#2563eb' }}
                  />
                )}

                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>
                  {isTerminal ? '' : `作业 ID: ${job.jobId.slice(-8)}`}
                </div>
              </div>
            );
          })}
        </>
      )}
    </div>
  );
};
